import { useState } from "react";
import { Sparkle, ChatCircleText, ThumbsUp, ThumbsDown } from "@phosphor-icons/react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { renderKbContent } from "./kbMarkdown";

/** Plain-English Q&A grounded in the caller's role-specific KB. Citations open via `onOpen(slug)`. */
export default function KbAskBox({ role, onOpen, testIdPrefix = "portal-help" }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [vote, setVote] = useState(null);
  const [comment, setComment] = useState("");
  const [showComment, setShowComment] = useState(false);
  const tid = (s) => `${testIdPrefix}-ask-${s}`;

  const ask = async () => {
    if (q.trim().length < 3) return;
    setLoading(true);
    setAnswer(null);
    setVote(null);
    setComment("");
    setShowComment(false);
    try {
      const { data } = await API.post(`/kb/ask`, { question: q.trim(), role });
      setAnswer(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't get an answer right now");
    } finally {
      setLoading(false);
    }
  };

  const sendFeedback = async (helpful) => {
    if (!answer?.ask_id || vote) return;
    if (!helpful && !showComment) { setShowComment(true); return; }
    try {
      await API.post(`/kb/ask/${answer.ask_id}/feedback`, { helpful, comment: helpful ? "" : comment.trim() });
      setVote(helpful ? "yes" : "no");
      toast.success(helpful ? "Thanks for the feedback!" : "Reported — we'll improve this.");
    } catch {
      toast.error("Couldn't record feedback");
    }
  };

  return (
    <div className="border border-indigo-200 dark:border-indigo-800 rounded-md bg-indigo-50/60 dark:bg-indigo-950/30 p-4 space-y-3" data-testid={tid("box")}>
      <div className="flex items-center gap-1.5 text-xs font-medium text-indigo-900 dark:text-indigo-200">
        <Sparkle size={14} weight="fill" className="text-indigo-500" /> Ask a question in plain English
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <textarea
          value={q}
          onChange={(e) => setQ(e.target.value.slice(0, 500))}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
          placeholder="e.g. How do I book an extra visit at short notice?"
          rows={2}
          className="flex-1 text-sm rounded-md border border-border bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          data-testid={tid("input")}
        />
        <Button onClick={ask} disabled={loading || q.trim().length < 3} size="sm" className="sm:self-end" data-testid={tid("submit")}>
          {loading ? "Thinking…" : (<><ChatCircleText size={14} className="mr-1" /> Ask</>)}
        </Button>
      </div>
      {answer && (
        <div className="border border-border rounded-md p-3 bg-card" data-testid={tid("answer")}>
          <div className="space-y-1" data-testid={tid("answer-text")}>{renderKbContent(answer.answer)}</div>
          {answer.citations?.length > 0 && (
            <div className="mt-3 pt-2 border-t border-border">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Read the guides</p>
              <div className="flex flex-col gap-1">
                {answer.citations.map((c, i) => (
                  <button key={c.slug} type="button" onClick={() => onOpen(c.slug)} className="text-xs text-primary hover:underline text-left" data-testid={tid(`citation-${i}`)}>
                    [{i + 1}] {c.title}
                  </button>
                ))}
              </div>
            </div>
          )}
          {answer.ask_id && (
            <div className="mt-3 pt-2 border-t border-border" data-testid={tid("feedback")}>
              {vote ? (
                <p className="text-[11px] text-muted-foreground italic">{vote === "yes" ? "Glad that helped." : "Reported — we'll improve this."}</p>
              ) : (
                <>
                  <p className="text-[11px] text-muted-foreground mb-1.5">Did this answer help?</p>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => sendFeedback(true)} data-testid={tid("vote-yes")}><ThumbsUp size={12} className="mr-1" /> Yes</Button>
                    <Button size="sm" variant="outline" onClick={() => sendFeedback(false)} data-testid={tid("vote-no")}><ThumbsDown size={12} className="mr-1" /> No</Button>
                  </div>
                  {showComment && (
                    <div className="mt-2 space-y-1.5" data-testid={tid("comment-box")}>
                      <textarea value={comment} onChange={(e) => setComment(e.target.value.slice(0, 500))} rows={2}
                        placeholder="Optional — what were you looking for?"
                        className="w-full text-xs rounded border border-border bg-background px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary"
                        data-testid={tid("comment-input")} />
                      <Button size="sm" onClick={() => sendFeedback(false)} data-testid={tid("send-feedback")}>Send feedback</Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
